/**
 * Avilexx "Kies een product met korting!" popup.
 *
 * Hooks into Impact's cart event system:
 *  - cart:prepare-bundled-sections — registers this section so its rendered HTML
 *    (containing fresh set data) is included with every cart-update fetch.
 *  - variant:add — fired when the user adds a product to cart. We inspect the
 *    just-added items, look up their set_partners metafield via the bundled
 *    section JSON, and show a single-select popup so the customer picks ONE
 *    partner product to redeem their 10% korting on.
 *
 * The chosen partner is added with `_set_addition: <triggerHandle>` line item
 * property. A BXGY discount in Shopify admin (one per partner product) then
 * applies the -10% based on the trigger product being in the cart.
 */
class SetPopup extends HTMLElement {
  static DISMISS_KEY = 'avilexx_set_popup_dismissed';
  static SECTION_ID = 'set-popup';
  static RADIO_NAME = 'set-popup-partner';

  constructor() {
    super();
    this._onPrepareBundledSections = this._onPrepareBundledSections.bind(this);
    this._onVariantAdd = this._onVariantAdd.bind(this);
    this._currentPartners = [];
    this._currentTrigger = null;
    this._selectedHandle = null;
    this._currency = 'EUR';
    this._discountPercent = 10;
    this._locale = (document.documentElement.getAttribute('lang') || 'nl-NL').replace('_', '-');
  }

  connectedCallback() {
    this.dialog = this.querySelector('[data-set-popup-dialog]');
    this.partnersEl = this.querySelector('[data-set-popup-partners]');
    this.triggerThumbEl = this.querySelector('[data-set-popup-trigger-thumb]');
    this.triggerTitleEl = this.querySelector('[data-set-popup-trigger-title]');
    this.addBtn = this.querySelector('[data-set-popup-add]');
    this.closeBtns = this.querySelectorAll('[data-set-popup-close]');
    this.dontShowCheckbox = this.querySelector('[data-set-popup-dont-show]');
    this.totalOriginalEl = this.querySelector('[data-set-popup-total-original]');
    this.totalFinalEl = this.querySelector('[data-set-popup-total-final]');

    this.closeBtns.forEach((btn) => btn.addEventListener('click', () => this._hide()));
    this.addBtn?.addEventListener('click', () => this._addPartnerToCart());
    this.dialog?.addEventListener('close', () => this._handleDismiss());

    document.addEventListener('cart:prepare-bundled-sections', this._onPrepareBundledSections);
    document.addEventListener('variant:add', this._onVariantAdd);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:prepare-bundled-sections', this._onPrepareBundledSections);
    document.removeEventListener('variant:add', this._onVariantAdd);
  }

  _onPrepareBundledSections(event) {
    if (!event.detail.sections.includes(SetPopup.SECTION_ID)) {
      event.detail.sections.push(SetPopup.SECTION_ID);
    }
  }

  _onVariantAdd(event) {
    if (this._isDismissedPermanently()) return;

    const addedItems = event.detail?.items || [];
    if (addedItems.length === 0) return;

    const setData = this._readSetData(event.detail?.cart);
    if (!setData || !Array.isArray(setData.triggers)) return;

    if (setData.currency) this._currency = setData.currency;
    if (typeof setData.discountPercent === 'number') this._discountPercent = setData.discountPercent;

    const cartHandles = new Set(setData.cartHandles || []);

    // Trigger products are matched on product handle. addedItems can come
    // from /cart/add.js as `{product_id, handle, ...}` or `{handle, ...}`.
    for (const added of addedItems) {
      const handle = added.handle || added.product_handle;
      if (!handle) continue;

      const trigger = setData.triggers.find((t) => t.triggerHandle === handle);
      if (!trigger) continue;

      const choices = trigger.partners.filter(
        (p) => p.available && p.variantId && !cartHandles.has(p.handle)
      );
      if (choices.length === 0) continue;

      this._show(trigger, choices);
      return;
    }
  }

  _readSetData(cart) {
    const sectionHtml = cart?.sections?.[SetPopup.SECTION_ID];
    if (!sectionHtml) {
      return this._readSetDataFromDom(this);
    }
    const tmp = document.createElement('div');
    tmp.innerHTML = sectionHtml;
    return this._readSetDataFromDom(tmp);
  }

  _readSetDataFromDom(root) {
    const script = root.querySelector('[data-set-popup-data]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      console.error('[set-popup] Failed to parse set data', err);
      return null;
    }
  }

  _isDismissedPermanently() {
    try {
      return localStorage.getItem(SetPopup.DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  }

  _formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(this._locale, {
        style: 'currency',
        currency: this._currency,
      }).format(amount);
    } catch {
      return `${this._currency} ${amount.toFixed(2)}`;
    }
  }

  _show(trigger, choices) {
    this._currentPartners = choices;
    this._currentTrigger = trigger;
    this._selectedHandle = choices[0]?.handle || null;

    // Trigger row
    if (this.triggerThumbEl) {
      this.triggerThumbEl.innerHTML = trigger.triggerImage
        ? `<img src="${trigger.triggerImage}" alt="${this._escape(trigger.triggerTitle)}" loading="lazy" width="120" height="120" />`
        : '';
    }
    if (this.triggerTitleEl) {
      this.triggerTitleEl.textContent = trigger.triggerTitle || '';
    }

    // Partner choices
    this.partnersEl.innerHTML = choices
      .map((p) => this._renderPartnerRow(p))
      .join('');

    this.partnersEl.querySelectorAll('[data-set-popup-partner-radio]').forEach((input) => {
      input.addEventListener('change', () => this._onPartnerSelect(input.value));
    });

    // Card-level click acts as a label for the radio, but lets <a> clicks
    // (thumb + title) still navigate to the PDP.
    this.partnersEl.querySelectorAll('[data-set-popup-partner]').forEach((card) => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('a, [data-set-popup-partner-radio]')) return;
        const handle = card.getAttribute('data-handle');
        if (handle) this._selectPartner(handle);
      });
    });

    this._updateTotals();

    if (this.dontShowCheckbox) this.dontShowCheckbox.checked = false;

    if (typeof this.dialog.showModal === 'function' && !this.dialog.open) {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
  }

  _renderPartnerRow(p) {
    const cents = Number(p.priceCents) || 0;
    const discounted = Math.round(cents * (1 - this._discountPercent / 100));
    const variant = p.variantTitle && p.variantTitle !== 'Default Title' ? p.variantTitle : '';
    const originalFormatted = this._formatMoney(cents);
    const discountedFormatted = this._formatMoney(discounted);
    const checked = p.handle === this._selectedHandle ? 'checked' : '';

    return `
      <li class="set-popup__partner" data-set-popup-partner data-handle="${this._escape(p.handle)}">
        <a href="${this._escape(p.url)}" class="set-popup__thumb" tabindex="-1">
          <img src="${this._escape(p.image)}" alt="${this._escape(p.title)}" loading="lazy" width="120" height="120" />
        </a>
        <div class="set-popup__row-info">
          <a href="${this._escape(p.url)}" class="set-popup__row-title">${this._escape(p.title)}</a>
          ${variant ? `<span class="set-popup__row-variant">${this._escape(variant)}</span>` : ''}
        </div>
        <div class="set-popup__partner-prices">
          <span class="set-popup__partner-price-original">
            <span class="set-popup__discount-tag">-${this._discountPercent}%</span>
            <span class="set-popup__price-strike">${originalFormatted}</span>
          </span>
          <span class="set-popup__partner-price-final">${discountedFormatted}</span>
        </div>
        <input
          type="radio"
          class="set-popup__partner-radio"
          name="${SetPopup.RADIO_NAME}"
          value="${this._escape(p.handle)}"
          data-set-popup-partner-radio
          aria-label="${this._escape(p.title)}"
          ${checked}
        />
      </li>
    `;
  }

  _onPartnerSelect(handle) {
    this._selectPartner(handle);
  }

  _selectPartner(handle) {
    if (!handle) return;
    this._selectedHandle = handle;
    // Sync the radio input state for click-on-card flow.
    this.partnersEl.querySelectorAll('[data-set-popup-partner-radio]').forEach((input) => {
      input.checked = input.value === handle;
    });
    this._updateTotals();
  }

  _updateTotals() {
    const selected = this._currentPartners.find((p) => p.handle === this._selectedHandle);
    const cents = selected ? Number(selected.priceCents) || 0 : 0;
    const discountedCents = Math.round(cents * (1 - this._discountPercent / 100));

    if (this.totalOriginalEl) {
      if (selected && cents > 0) {
        this.totalOriginalEl.innerHTML = `
          <span class="set-popup__discount-tag">-${this._discountPercent}%</span>
          <span class="set-popup__price-strike">${this._formatMoney(cents)}</span>
        `;
      } else {
        this.totalOriginalEl.innerHTML = '';
      }
    }
    if (this.totalFinalEl) {
      this.totalFinalEl.textContent = this._formatMoney(discountedCents);
    }
    if (this.addBtn) {
      this.addBtn.disabled = !selected;
    }
  }

  _hide() {
    if (typeof this.dialog.close === 'function' && this.dialog.open) {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
      this._handleDismiss();
    }
  }

  _handleDismiss() {
    if (this.dontShowCheckbox?.checked) {
      try {
        localStorage.setItem(SetPopup.DISMISS_KEY, '1');
      } catch {
        /* ignore */
      }
    }
  }

  async _addPartnerToCart() {
    const selected = this._currentPartners.find((p) => p.handle === this._selectedHandle);
    if (!selected) return;

    this.addBtn.disabled = true;
    this.addBtn.setAttribute('aria-busy', 'true');

    // Ask other components which sections need refreshing after the add.
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );
    const sectionsParam = [...new Set(sectionsToBundle)].join(',');

    const triggerHandle = this._currentTrigger?.triggerHandle || '';
    const items = [
      {
        id: selected.variantId,
        quantity: 1,
        properties: { _set_addition: triggerHandle || 'true' },
      },
    ];

    try {
      const addUrl = `${window.Shopify.routes.root}cart/add.js`;
      const addResp = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionsParam || undefined }),
      });
      if (!addResp.ok) throw new Error(`cart/add failed: ${addResp.status}`);
      const addData = await addResp.json();

      const cartUrl = `${window.Shopify.routes.root}cart.js`;
      const cartResp = await fetch(cartUrl);
      const cart = await cartResp.json();
      cart.sections = addData.sections || {};

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'set-popup:add', cart },
        })
      );

      // Force a fresh cart-drawer fetch. The bundled sections returned by
      // /cart/add.js can have line-item keys that drift from the server's
      // /cart.js keys when automatic Buy-X-Get-Y discounts apply, which
      // breaks the remove-from-cart buttons. A fresh fetch realigns them.
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      this._hide();
    } catch (err) {
      console.error('[set-popup] Failed to add partner', err);
    } finally {
      this.addBtn.disabled = false;
      this.addBtn.removeAttribute('aria-busy');
    }
  }

  _escape(str) {
    return String(str ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[c]);
  }
}

if (!customElements.get('set-popup')) {
  customElements.define('set-popup', SetPopup);
}
